/**
 * External dependencies
 */
import {
  createSolid,
  generatePatternStyles,
  isGradientGeometryV2,
} from '@web-stories-wp/patterns';
import type { Pattern } from '@web-stories-wp/patterns';

export const GRADIENT_PATTERN_STYLE_PROP = '--gradient-pattern';
export const LEGACY_GRADIENT_PATTERN_STYLE_PROP = '--ws-text-gradient';
export const GRADIENT_PATTERN_STYLE_PROPS = [
  GRADIENT_PATTERN_STYLE_PROP,
  LEGACY_GRADIENT_PATTERN_STYLE_PROP,
];
export const DEFAULT_GRADIENT_FALLBACK_COLOR = '#000000';

export function encodeGradientPattern(pattern: Pattern) {
  return encodeURIComponent(JSON.stringify(pattern));
}

export function decodeGradientPattern(value?: string | null): Pattern | null {
  if (!value) {
    return null;
  }
  try {
    return JSON.parse(decodeURIComponent(value)) as Pattern;
  } catch (e) {
    return null;
  }
}

export function getGradientFallbackColor(
  pattern?: Pattern | null
): string | null {
  const stopColor = pattern?.stops?.[0]?.color;
  if (!stopColor) {
    return null;
  }
  const { r, g, b, a } = stopColor;
  const { color } = generatePatternStyles(
    createSolid(r, g, b, a ?? 1),
    'color'
  );
  return color || null;
}

export function getGradientPatternStyleValue(
  style?: CSSStyleDeclaration | null
): string {
  if (!style) {
    return '';
  }

  for (const prop of GRADIENT_PATTERN_STYLE_PROPS) {
    const value = style.getPropertyValue(prop).trim();
    if (value) {
      return value;
    }
  }

  return '';
}

export function removeGradientPatternStyleProps(
  style?: CSSStyleDeclaration | null
): void {
  if (!style) {
    return;
  }

  GRADIENT_PATTERN_STYLE_PROPS.forEach((prop) => {
    style.removeProperty(prop);
  });
}

export function hasGradientPatternString(value?: string | null): boolean {
  if (!value) {
    return false;
  }

  return GRADIENT_PATTERN_STYLE_PROPS.some((prop) => value.includes(prop));
}

export function getGradientPatternStyleSelector(): string {
  return GRADIENT_PATTERN_STYLE_PROPS.map((prop) => `[style*="${prop}"]`).join(
    ','
  );
}

export function hasVersionedInlineGradient(content?: string | null): boolean {
  if (!content || !hasGradientPatternString(content)) return false;
  const values = content.matchAll(
    /--(?:gradient-pattern|ws-text-gradient)\s*:\s*([^;"']+)/g
  );
  return Array.from(values).some((match) =>
    isGradientGeometryV2(decodeGradientPattern(match[1].trim()))
  );
}
